<template>
  <div class="app-content">
    <div id="app" class="rready-application" data-app>
      <div class="rready-application--wrap">
        <app-header :max-width="maxPortalWidth" />
        <div class="fill-height content-container">
          <div :style="portalStyle" class="app-body">
            <router-view />
          </div>
        </div>
        <chat-bot v-if="showChatBot" />
        <app-chat v-if="showAppChat" class="app-chat" />
        <app-footer :max-width="maxPortalWidth" />

        <inspiration-modal
          v-if="inspirationParent"
          :parent-id="inspirationParent"
          @close="inspirationParent = ''"
        />

        <modal-launcher
          v-if="modalOpened"
          :content="modalContent"
          :context-id="contextId"
          :product-id="productId"
          :type="modalType"
          :user-id="currentUser.id"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, provide } from 'vue';
import type {
  IAiAgentConfig,
  INotificationModalResource,
  IOrganization,
  IUser
} from '@rready/sdk';
import { sentry } from '@rready/integration';
import { useAiAgents, useProducts } from '@rready/composables';
import { modalCaptureMixin } from './mixin/modalCaptureMixin';
import AppFooter from './components/structure/AppFooter.vue';
import AppHeader from './components/structure/AppHeader.vue';
import ChatBot from './components/widgets/ChatBot.vue';
import AppChat from './components/chat/AppChat.vue';
import InspirationModal from './components/widgets/InspirationModal.vue';
import { useAuthStore } from './store/authStore';
import { useContextStore } from './store/contextStore';
import { useProductStore } from './store/productStore';
import { useAiAgentStore } from './store/aiAgentStore';

export default defineComponent({
  components: {
    AppHeader,
    AppFooter,
    AppChat,
    InspirationModal,
    ChatBot
  },
  mixins: [modalCaptureMixin],
  data() {
    return {
      ready: false,
      inspirationParent: '',
      aiAgentConfig: {} as IAiAgentConfig
    };
  },
  watch: {
    $route: {
      handler() {
        // Loads the AI agent for this page
        if (this.enableAgents) {
          this.setPageAiAgents();
        }
        // Opens the feedback modal
        if (this.$route.hash?.startsWith('#feedback=')) {
          const ticketId = this.$route.hash.replace('#feedback=', '');
          this.openFeedbackModal(ticketId);
        }
        // Opens the inspiration modal
        if (
          this.$route.hash?.startsWith('#inspire-me=') &&
          this.enableUserInspirePeople
        ) {
          const ticketId = this.$route.hash.replace('#inspire-me=', '');
          this.$router.replace({
            hash: ''
          });
          this.openInspirationModal(ticketId);
        }
      },
      immediate: true
    }
  },
  async created(): Promise<void> {
    try {
      this.checkCompleteUserProfile();
      this.loadAnalytics();
      provide(
        'maxFileUploadSize',
        this.organization?.settings?.maxFileUploadSize ?? 52428800
      ); // Default 50MB
      if (this.enableAgents) {
        await this.loadAiAgentSettings();
      }
      this.setPageAiAgents();
    } catch (e) {
      this.$sentry.captureException(e);
      console.error('Could not load user authentication', e);
    }
  },
  computed: {
    currentUser(): IUser {
      return useAuthStore().user?.raw;
    },
    organization(): IOrganization {
      return useContextStore().organization;
    },
    contextId(): string {
      return useContextStore().contextId!;
    },
    productId(): string {
      return useProductStore().productId!;
    },
    enableAgents(): boolean {
      return !!useAuthStore().permission?.checkPermission([
        'USER_AI_AGENTS_READ'
      ]);
    },
    enableUserInspirePeople(): boolean {
      return !!(
        this.organization.productCustomization[this.productId]
          .enableAiContentGeneration &&
        this.organization.settings?.userInspirePeople
      );
    },
    maxPortalWidth(): number {
      const portalWidth = this.organization.settings?.portalWidth;
      return portalWidth === 'full-width' ? 1920 : 1000;
    },
    portalStyle(): string {
      return `max-width: ${this.maxPortalWidth}px`;
    },
    showAppChat(): boolean {
      return useAiAgentStore().pageAgents.length > 0;
    },
    showChatBot(): boolean {
      // The chatBot will be enabled for these companies until we have a BE for it
      // https://rready.atlassian.net/browse/IP-4960
      const contextArray = [
        'rready',
        'rreadysandbox',
        'rreadybkp',
        'rreadytest',
        'rreadyPlatformDemo',
        'TalentKick',
        'bmwschweiz',
        'bmwusa'
      ];
      const currentContext = this.organization?.context ?? '';
      return (
        this.productId === 'kickbox' && contextArray.includes(currentContext)
      );
    }
  },
  methods: {
    async loadAiAgentSettings(): Promise<void> {
      const agents = await this.$api.aiAgent.getAllAgents();

      const { productsData } = useProducts(this.organization);
      this.aiAgentConfig =
        productsData.value.find(({ productId }) => productId === this.productId)
          ?.customization?.ai?.agents ?? ({} as IAiAgentConfig);

      useAiAgentStore().provideAgents(agents);
    },
    setPageAiAgents(): void {
      if (this.aiAgentConfig.visibility) {
        const currentPages = this.$router.currentRoute.matched
          .map((m) => m.name)
          .filter(Boolean) as string[];
        const requiredAgentIds = useAiAgents(
          this.aiAgentConfig
        ).getAgentsForPages([...currentPages, 'default']);

        useAiAgentStore().setPageAgentIds(requiredAgentIds);
        const context = { user: this.currentUser };
        const { pageAgents, provideContext } = useAiAgentStore();
        pageAgents.forEach(({ id }) => provideContext(context, id));
      }
    },
    checkCompleteUserProfile(): void {
      const { user } = useAuthStore();
      if (!user?.profileCompleted()) {
        this.$router.push({ name: 'CompleteProfile' });
      }
    },
    loadAnalytics(): void {
      const { id, language } = useAuthStore().user!;
      const context = useContextStore().contextId!;

      sentry.config(id!, language!, context);
    },
    async openFeedbackModal(ticketId: string): Promise<void> {
      const ticket = await this.$gateway.feedback.loadFeedbackTicketById(
        this.productId,
        ticketId
      );

      if (
        await this.$gateway.feedback.isFeedbackAllowed(
          ticket,
          this.currentUser.id
        )
      ) {
        const resource = ticket.meta.feedbackDialogue;
        const modalResource = this.$res(resource) as INotificationModalResource;
        this.$modal(ticket.meta['feedback-type'], modalResource);
      }
    },
    openInspirationModal(ticketId: string): void {
      this.inspirationParent = ticketId;
    }
  }
});
</script>

<style lang="scss">
@import './compat/vtify-reset.scss';
@import '@rready/design/dist/assets/global.scss';
@import '@rready/design/dist/style.css';
@import '@rready/components/dist/style.css';
@import '@rready/dynamic-fields/dist/style.css';
@import './compat/design-library-compat.scss';
@import '@rready/components/dist/assets/structure.scss';

.app {
  font-family: 'Montserrat', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #ffffff;
  color: #333333;
}

a {
  text-decoration: none;
  color: var(--color-blue);
}

.app-chat {
  z-index: 10;
}

.app-body {
  width: 100%;
}

.centered-page {
  margin-top: 0;
}

.rready-application {
  display: flex;
  position: relative;

  .rready-application--wrap {
    flex: 1 1 auto;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    max-width: 100%;
    position: relative;
  }

  .content-container {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    flex: 1 1 auto;
    padding: 0 24px;
  }
}
</style>
